
import React, { useState, useEffect, useMemo } from 'react';
import { raceEventService } from '../services/raceService';
import type { RaceEvent, ExternalEvent } from '../types';
import type { InitialRaceData } from '../components/AddRaceModal';
import RaceCard from '../components/RaceCard';
import ExternalEventCard from '../components/ExternalEventCard';
import { useAuth } from '../contexts/AuthContext';
import RaceEditorModal from '../components/AddRaceModal';
import CategorySelectionModal from '../components/CategorySelectionModal';
import { eventIntegrationService } from '../services/eventIntegrationService';
import { userService } from '../services/userService';
import SEO from '../components/SEO';

// Schema Markup for Search Engines and AI
const structuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "RaceLineIQ",
  "applicationCategory": "SportsAnalytics",
  "operatingSystem": "Web",
  "description": "RaceLineIQ is the first observability platform that correlates your performance with the entire pack to reveal the tactical insights behind every race result.",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock"
  }
};

const FindRacesPage: React.FC = () => {
    const { currentUser, isAdmin } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Internal Race State
    const [races, setRaces] = useState<RaceEvent[]>([]);
    const [isLoadingRaces, setIsLoadingRaces] = useState(true);
    const [customRaceIds, setCustomRaceIds] = useState<Set<string>>(new Set());

    // External Event State
    const [externalEvents, setExternalEvents] = useState<ExternalEvent[]>([]);
    const [isSearchingExternal, setIsSearchingExternal] = useState(false);
    const [plannedEventIds, setPlannedEventIds] = useState<Set<string>>(new Set());
    const [processingEventId, setProcessingEventId] = useState<string | null>(null);
    
    // Modals State
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<RaceEvent | undefined>(undefined);
    const [initialRaceData, setInitialRaceData] = useState<InitialRaceData | null>(null);

    const [isSelectionOpen, setIsSelectionOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<RaceEvent | null>(null);

    // --- Debounce Search Term ---
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 600);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // --- Load Internal Races ---
    const loadRaces = async () => {
        setIsLoadingRaces(true);
        try {
            const allEvents = await raceEventService.getAllRaceEvents();
            setRaces(allEvents);

            const standardIds = new Set(['emerald-city-classic', 'cascade-climbfest', 'puget-sound-gp', 'rainier-road-race', 'giro-de-san-francisco']);
            const custom = new Set<string>(allEvents.filter(r => !standardIds.has(r.id)).map(r => r.id));
            setCustomRaceIds(custom);
        } catch (e) {
            console.error("Failed to load races", e);
        } finally {
            setIsLoadingRaces(false);
        }
    };

    // --- Load User Calendar for status checks ---
    useEffect(() => {
        const loadUserCalendar = async () => {
            if (currentUser) {
                const profile = await userService.getUserProfile(currentUser.uid);
                if (profile && profile.plannedEvents) {
                    setPlannedEventIds(new Set(profile.plannedEvents.map(e => e.id)));
                }
            }
        };
        loadUserCalendar();
    }, [currentUser]);

    useEffect(() => {
        loadRaces();
    }, []);

    const [eventTypeFilter, setEventTypeFilter] = useState<'all' | 'race' | 'group_ride'>('all');

    // --- Filtering ---
    const filteredRaces = useMemo(() => {
        // Per-date group ride occurrences are reached from their series, not listed in the catalog
        let result = races.filter(r => !r.seriesId);

        if (eventTypeFilter === 'race') {
            result = result.filter(r => r.eventType !== 'group_ride');
        } else if (eventTypeFilter === 'group_ride') {
            result = result.filter(r => r.eventType === 'group_ride');
        }

        if (debouncedSearchTerm.trim()) {
            const lowerTerm = debouncedSearchTerm.toLowerCase();
            result = result.filter(r => 
                r.name.toLowerCase().includes(lowerTerm) || 
                r.groupRideDetails?.locationName?.toLowerCase().includes(lowerTerm)
            );
        }

        return result;
    }, [races, debouncedSearchTerm, eventTypeFilter]);

    // --- Search External API ---
    useEffect(() => {
        if (!debouncedSearchTerm.trim()) {
            setExternalEvents([]);
            return;
        }

        const fetchExternal = async () => {
            setIsSearchingExternal(true);
            try {
                const results = await eventIntegrationService.searchEvents(debouncedSearchTerm);
                setExternalEvents(results);
            } catch (error) {
                console.error("External search failed", error);
            } finally {
                setIsSearchingExternal(false);
            }
        };

        fetchExternal();
    }, [debouncedSearchTerm]);


    // --- Handlers ---

    const handleOpenEditModal = async (eventToEdit: RaceEvent) => {
        setIsLoadingRaces(true);
        try {
            const fullEvent = await raceEventService.getRaceEventBySlug(eventToEdit.id);
            if (fullEvent) {
                setEditingEvent(fullEvent);
                setInitialRaceData(null);
                setIsEditorOpen(true);
            } else {
                alert("Could not load full event details.");
            }
        } catch (error) {
            console.error("Error fetching full race details:", error);
            alert("An error occurred while loading event details.");
        } finally {
            setIsLoadingRaces(false);
        }
    };

    const handleSelectGroup = (race: RaceEvent) => {
        setSelectedEvent(race);
        setIsSelectionOpen(true);
    }

    const handleCreateFromExternal = (event: ExternalEvent) => {
        setEditingEvent(undefined);
        setInitialRaceData({
            name: event.name,
            date: event.date,
            category: 'Category 1/2/3'
        });
        setIsEditorOpen(true);
    }

    const handlePlanEvent = async (event: ExternalEvent) => {
        if (!currentUser) return;
        setProcessingEventId(event.id);
        try {
            await userService.addPlannedEvent(currentUser.uid, event);
            setPlannedEventIds(prev => new Set(prev).add(event.id));
        } catch (e) {
            alert("Failed to add to calendar.");
        } finally {
            setProcessingEventId(null);
        }
    };

    const handleCloseModal = () => {
        setIsEditorOpen(false);
        setEditingEvent(undefined);
        setInitialRaceData(null);
    }

    const handleSaveEvent = async (event: RaceEvent) => {
      try {
          await raceEventService.saveRaceEvent(event);
          await loadRaces();
          handleCloseModal();
      } catch (error) {
          alert("Could not save the race. Please try again.");
      }
    };

    const handleDeleteEvent = async (event: RaceEvent) => {
      try {
          await raceEventService.deleteRaceEvent(event.id);
          await loadRaces();
          handleCloseModal();
      } catch (error) {
          alert("Could not delete the race. Please try again.");
      }
    };

    return (
        <div className="h-full bg-gray-900 text-white overflow-y-auto">
            <SEO />
            {/* Inject JSON-LD Schema Markup */}
            <script 
                type="application/ld+json" 
                dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} 
            />

            {/* Hero Search Section */}
            <div className="bg-gray-800 border-b border-gray-700 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl font-extrabold text-white sm:text-5xl mb-6">
                        Find Events
                    </h1>
                    <p className="text-lg text-gray-400 mb-6 max-w-2xl mx-auto">
                        Discover competitive races and recurring group rides, view route telemetry, or download GPX files.
                    </p>
                    <div className="relative max-w-2xl mx-auto mb-6">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-4 border border-gray-600 rounded-lg leading-5 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:bg-gray-800 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 sm:text-lg shadow-lg transition-all"
                            placeholder="Search by event or ride name, location..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Event Type Filter Chips */}
                    <div className="flex flex-wrap justify-center gap-2">
                        <button
                            onClick={() => setEventTypeFilter('all')}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${eventTypeFilter === 'all' ? 'bg-cyan-500 text-gray-950 border-cyan-400 shadow' : 'bg-gray-900 text-gray-300 border-gray-700 hover:border-gray-500 hover:text-white'}`}
                        >
                            All Events
                        </button>
                        <button
                            onClick={() => setEventTypeFilter('race')}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border flex items-center gap-1.5 ${eventTypeFilter === 'race' ? 'bg-cyan-500 text-gray-950 border-cyan-400 shadow' : 'bg-gray-900 text-gray-300 border-gray-700 hover:border-gray-500 hover:text-white'}`}
                        >
                            <span>🏁</span> Races
                        </button>
                        <button
                            onClick={() => setEventTypeFilter('group_ride')}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border flex items-center gap-1.5 ${eventTypeFilter === 'group_ride' ? 'bg-cyan-500 text-gray-950 border-cyan-400 shadow' : 'bg-gray-900 text-gray-300 border-gray-700 hover:border-gray-500 hover:text-white'}`}
                        >
                            <span>🚴</span> Group Rides
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-12">
                
                {/* 1. OFFICIAL CALENDAR RESULTS */}
                {(isSearchingExternal || externalEvents.length > 0) && (
                    <section>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <span className="text-cyan-400">Official Calendar</span> Results
                                {isSearchingExternal && (
                                     <svg className="animate-spin h-4 w-4 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                            </h2>
                            <span className="text-xs text-gray-500">Powered by Outside</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {externalEvents.map(event => (
                                <ExternalEventCard 
                                    key={event.id}
                                    event={event}
                                    onPlan={handlePlanEvent}
                                    onCreate={handleCreateFromExternal}
                                    isPlanned={plannedEventIds.has(event.id)}
                                    isProcessing={processingEventId === event.id}
                                    showCreate={isAdmin}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* 2. COMMUNITY RACES RESULTS */}
                <section>
                    <div className="flex items-end justify-between mb-6">
                         <h2 className="text-xl font-bold text-gray-300">
                             Community Races {filteredRaces.length > 0 && `(${filteredRaces.length})`}
                        </h2>
                    </div>

                    {isLoadingRaces ? (
                        <div className="flex justify-center items-center h-32">
                             <svg className="animate-spin h-8 w-8 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : filteredRaces.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredRaces.map(race => (
                                     <RaceCard
                                         key={race.id}
                                         race={race}
                                         isCustom={customRaceIds.has(race.id)}
                                         currentUserId={currentUser?.uid}
                                         onEdit={handleOpenEditModal}
                                         onSelectGroup={handleSelectGroup}
                                     />
                            ))}
                        </div>
                    ) : (
                         // If we are searching and found no internal races (but maybe found external ones)
                         searchTerm && externalEvents.length > 0 ? (
                            <p className="text-gray-500 italic">No community races found matching "{searchTerm}".</p>
                         ) : (
                             // No results at all
                            <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-dashed border-gray-700">
                                <p className="text-xl text-gray-400">No races found matching "{searchTerm}"</p>
                                <button 
                                    onClick={() => setSearchTerm('')}
                                    className="mt-4 text-cyan-400 hover:text-cyan-300 font-medium underline"
                                >
                                    Clear Search
                                </button>
                            </div>
                         )
                    )}
                </section>
            </div>

            {isEditorOpen && (
                <RaceEditorModal
                    onSave={handleSaveEvent}
                    onClose={handleCloseModal}
                    onDelete={handleDeleteEvent}
                    eventToEdit={editingEvent}
                    initialData={initialRaceData}
                />
            )}

            {isSelectionOpen && selectedEvent && (
                <CategorySelectionModal
                    isOpen={isSelectionOpen}
                    onClose={() => setIsSelectionOpen(false)}
                    race={selectedEvent}
                />
            )}
        </div>
    );
};

export default FindRacesPage;
